import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAIPicks, getPosterUrl, manhuaCover } from '../services/api';
import { trackEvent } from '../services/analytics';
import { useRegion } from '../contexts/RegionContext';
import { pickProviderBlock } from '../services/regions';

/**
 * "The Brief" — the editorial AI curation section (design handoff v2).
 *
 * Structure (A) + curator voice (B) + a visible, removable-token brief:
 *   1. The brief row   — base query pill + one gold "+ <refinement> ✕" token each
 *   2. The Shortlist   — header + count
 *   3. From the curator— opening note, then a correspondence thread (You: "darker" → reply)
 *   4. Picks           — 3 columns, gold numeral, poster, title, meta, italic reason
 *   5. Write back      — refine chips + a free-text input
 *
 * Real pipeline: query (+ merged refinements) → POST /curate/picks → { picks:[{id,why}], note }.
 * Refinements are an ordered list; removing one re-curates from the rest (step back).
 * Fails soft: no picks and no refinement → renders nothing (the grid below carries on).
 *
 * Props: query, results, kind ('movie'|'manhua'), onItemClick, took (string ms→s)
 */

// The editorial refine chips. Each becomes a removable token + a thread entry.
// `answers` is merged into the refinement_answers map the backend already accepts.
const REFINE_CHIPS = [
  { label: 'lighter', answers: { tone: 'lighter' } },
  { label: 'darker', answers: { tone: 'darker' } },
  { label: 'less violent', answers: { content: 'gentler, less violent' } },
  { label: 'more like the plot', answers: { focus: 'plot mechanics over vibe' } },
  { label: 'bigger, louder', answers: { scale: 'bigger, louder, epic' } },
];

const C = {
  ink: '#F1ECE2', cream: '#E5DFD2', soft: '#C9C2B4', sub: '#A39C8F',
  muted: '#8F887C', faint: '#6E675D', gold: '#DCA85E',
  hair: 'rgba(241,236,226,0.12)', hairStrong: 'rgba(241,236,226,0.18)',
};
const SERIF = "'Newsreader', Georgia, serif";

const micro = { fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.faint };

let _uid = 0;

// Merge an ordered refinement list into the {group: value, follow_up} map the API takes.
// Later entries win per key; free-text follow-ups accumulate (comma-joined).
const mergeAnswers = (refs) => {
  const a = {};
  const followUps = [];
  for (const r of refs) {
    if (r.followUp) followUps.push(r.followUp);
    else Object.assign(a, r.answers);
  }
  if (followUps.length) a.follow_up = followUps.join(', ');
  return Object.keys(a).length ? a : null;
};

const TheBrief = ({ query, results, kind = 'movie', onItemClick, took = '0.9' }) => {
  const [picks, setPicks] = useState([]);
  const [note, setNote] = useState('');         // opening curator note (base query)
  const [status, setStatus] = useState('idle'); // idle | loading | refining | ready | empty
  const [refs, setRefs] = useState([]);          // ordered refinements [{id,label,answers|followUp}]
  const [thread, setThread] = useState([]);      // [{ ask, note }] parallel to refs
  const [refineText, setRefineText] = useState('');
  const reqRef = useRef(0);
  const { region } = useRegion();

  const matchCount = results?.length || 0;

  // Reset the whole brief when the underlying search changes.
  useEffect(() => { setRefs([]); setThread([]); setRefineText(''); }, [query]);

  const answers = useMemo(() => mergeAnswers(refs), [refs]);
  const answersKey = useMemo(() => JSON.stringify(answers), [answers]);

  // (Re)curate whenever query / results / merged refinements change.
  useEffect(() => {
    if (!query || !results?.length) { setStatus('idle'); setPicks([]); return; }
    const reqId = ++reqRef.current;
    setStatus((s) => (s === 'idle' ? 'loading' : 'refining'));
    (async () => {
      const data = await getAIPicks(query, results, answers, 3);
      if (reqId !== reqRef.current) return; // stale
      const got = Array.isArray(data?.picks) ? data.picks : [];
      if (got.length === 0) {
        // The curator can return empty when the LLM is capped/rate-limited/parse-fails.
        // If we already have a good shortlist (i.e. this was a refinement), DON'T wipe
        // it — keep the last picks visible and stay 'ready' so refining still works.
        // Only go 'empty' on the very first (base) curation, where there's nothing yet.
        if (picks.length) {
          setStatus('ready');
          setThread((prev) => (prev.length
            ? prev.map((t, i) => (i === prev.length - 1 && !t.note
                ? { ...t, note: 'I couldn’t reshape the shortlist just now — keeping the last one. Try again, or remove a refinement.' }
                : t))
            : prev));
        } else {
          setStatus('empty');
          setPicks([]);
        }
        return;
      }
      setPicks(got);
      setStatus('ready');
      // Opening note only reflects the base query (no refinements). Refinement
      // replies are attached to the thread entry that triggered them.
      if (!answers) setNote(data?.note || '');
      else if (data?.note) {
        setThread((prev) => (prev.length ? prev.map((t, i) => (i === prev.length - 1 && !t.note ? { ...t, note: data.note } : t)) : prev));
      }
      trackEvent('AI Picks Shown', { query, kind, count: got.length, provider: data?.provider, refined: Boolean(answers) });
    })();
  }, [query, results, kind, answersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'idle') return null;
  if (status === 'empty' && !refs.length) return null;

  // Bind picks to real result rows by id.
  const byId = {};
  for (const r of results || []) {
    const rid = r?.id ?? r?.tmdb_id ?? r?.manhua_id;
    if (rid != null) byId[String(rid)] = r;
  }
  const loading = status === 'loading';
  const refining = status === 'refining';
  const tiles = loading
    ? (results || []).slice(0, 3).map((r) => ({ item: r, why: null }))
    : picks.map((p) => ({ item: byId[String(p.id)], why: p.why })).filter((x) => x.item);

  const posterFor = (it) => (kind === 'manhua' ? manhuaCover(it.poster_path, 512) : getPosterUrl(it.poster_path, 'w342'));
  const titleFor = (it) => it.title || it.name || '';
  const yearFor = (it) => it.year || ((it.release_date || it.first_air_date || '') + '').slice(0, 4);
  const ratingFor = (it) => Number(it.vote_average || it.rating || 0);
  const svcFor = (it) => {
    const wp = pickProviderBlock(it.watch_providers, region);
    const f = wp?.flatrate?.[0]?.provider_name;
    return f || (it.platforms?.[0]) || '';
  };

  const addRefine = (label, payload) => {
    // Allow refining whenever a request isn't already in flight — previously this
    // required status==='ready', which locked the user out after an empty response.
    if (status === 'loading' || status === 'refining') return;
    const id = ++_uid;
    setRefs((p) => [...p, { id, label, ...payload }]);
    setThread((p) => [...p, { ask: label, note: '' }]);
    trackEvent('Brief Refined', { kind, ask: label });
  };
  const onChip = (c) => addRefine(c.label, { answers: c.answers });
  const onSend = (e) => {
    e.preventDefault();
    const text = refineText.trim();
    if (!text) return;
    addRefine(text, { followUp: text });
    setRefineText('');
  };
  const removeToken = (id) => {
    const idx = refs.findIndex((r) => r.id === id);
    if (idx < 0) return;
    setRefs((p) => p.filter((r) => r.id !== id));
    setThread((p) => p.filter((_, i) => i !== idx));
    trackEvent('Brief Token Removed', { kind });
  };

  return (
    <div className="animate-rise" style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", color: C.ink }}>
      {/* 1 · The brief */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0 0', flexWrap: 'wrap' }}>
        <span style={micro}>The brief</span>
        <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 14.5, color: C.ink, border: `1px solid rgba(241,236,226,0.3)`, borderRadius: 99, padding: '5px 14px' }}>
          &ldquo;{query}&rdquo;
        </span>
        {refs.map((r) => (
          <span key={r.id} className="animate-fadein" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: SERIF, fontStyle: 'italic', fontSize: 14.5, color: C.gold, border: '1px solid rgba(220,168,94,0.5)', background: 'rgba(220,168,94,0.08)', borderRadius: 99, padding: '4px 8px 4px 14px' }}>
            + {r.label}
            <button onClick={() => removeToken(r.id)} title="Remove this refinement"
              style={{ cursor: 'pointer', background: 'transparent', border: 'none', color: C.gold, fontSize: 11, padding: '3px 7px', borderRadius: 99, lineHeight: 1 }}>
              ✕
            </button>
          </span>
        ))}
        {refs.length === 0 && (
          <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 13.5, color: C.faint }}>
            refinements pin here — remove one to step back
          </span>
        )}
      </div>

      {/* 2 · Section header */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', borderBottom: `1px solid ${C.hairStrong}`, padding: '26px 0 16px', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
          <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 40, margin: 0, letterSpacing: '-0.01em', lineHeight: 1.05 }}>The Shortlist</h2>
          <span style={{ fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.gold }}>
            3 of {matchCount} matches, chosen for you
          </span>
        </div>
        <span style={{ fontSize: 13, color: C.faint }}>matched in {took}s</span>
      </div>

      {/* 3 · From the curator */}
      <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 24, padding: '22px 0', borderBottom: `1px solid ${C.hair}`, maxWidth: 880 }}>
        <span style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: C.gold, paddingTop: 5 }}>From the curator</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontFamily: SERIF, fontSize: 18, lineHeight: 1.55, color: C.cream, margin: 0 }}>
            {note || (loading ? 'Reading your words against the index…' : 'Three worth your evening — each earns its place for a different reason.')}
          </p>
          {thread.map((t, i) => (
            <div key={i} className="animate-fadein" style={{ display: 'flex', flexDirection: 'column', gap: 6, borderTop: `1px solid rgba(241,236,226,0.1)`, paddingTop: 12 }}>
              <div style={{ fontSize: 13, color: C.sub }}><span style={{ color: C.faint }}>You:</span> &ldquo;{t.ask}&rdquo;</div>
              <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 16, lineHeight: 1.5, color: C.cream, margin: 0 }}>
                {t.note || (refining && i === thread.length - 1 ? 'Re-reading the shelf…' : '…')}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* 4 · Picks */}
      <div style={{ opacity: refining ? 0.3 : 1, transition: 'opacity 0.3s', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', borderBottom: `1px solid ${C.hairStrong}` }}>
        {tiles.map(({ item, why }, i) => (
          <button key={item.id ?? i} onClick={() => { trackEvent('AI Pick Clicked', { kind, id: item.id, title: titleFor(item) }); onItemClick && onItemClick(item); }}
            style={{ textAlign: 'left', cursor: 'pointer', background: 'transparent', padding: '26px 28px 30px', borderLeft: i === 0 ? 'none' : `1px solid rgba(241,236,226,0.14)`, borderTop: 'none', borderRight: 'none', borderBottom: 'none', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: SERIF, fontSize: 15, color: C.gold }}>0{i + 1}</span>
              <span style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint }}>{svcFor(item)}</span>
            </div>
            <div style={{ display: 'flex', gap: 18 }}>
              <div style={{ width: 92, flexShrink: 0, aspectRatio: '2/3', borderRadius: 4, overflow: 'hidden', background: 'rgba(241,236,226,0.06)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)' }}>
                {posterFor(item) && <img src={posterFor(item)} alt={titleFor(item)} loading="lazy" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 22, lineHeight: 1.1, margin: 0, letterSpacing: '-0.01em' }}>{titleFor(item)}</h3>
                <div style={{ fontSize: 12.5, color: C.sub }}>
                  {yearFor(item)}{ratingFor(item) > 0 ? ` · ★ ${ratingFor(item).toFixed(1)}` : ''}
                </div>
                <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 15.5, lineHeight: 1.45, color: C.soft, margin: '2px 0 0' }}>
                  {why || (loading ? '…' : (item.overview || '').split('. ')[0])}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* 5 · Write back */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 0 0', flexWrap: 'wrap' }}>
        <span style={micro}>Write back</span>
        {REFINE_CHIPS.map((c) => (
          <button key={c.label} onClick={() => onChip(c)}
            className="binge-chip"
            style={{ cursor: 'pointer', fontFamily: SERIF, fontStyle: 'italic', fontSize: 15, padding: '5px 14px', borderRadius: 99, border: '1px solid rgba(241,236,226,0.22)', background: 'transparent', color: C.soft, whiteSpace: 'nowrap' }}>
            {c.label}
          </button>
        ))}
        <form onSubmit={onSend} style={{ flex: 1, minWidth: 280, display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid rgba(241,236,226,0.25)', paddingBottom: 5 }}>
          <input value={refineText} onChange={(e) => setRefineText(e.target.value)} className="binge-input"
            placeholder="e.g. ‘bigger, louder, and more horror’"
            style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontFamily: SERIF, fontSize: 15, color: C.ink, padding: 0 }} />
          <button type="submit" style={{ cursor: 'pointer', background: 'transparent', border: 'none', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.gold, fontFamily: "'Instrument Sans', sans-serif" }}>Send</button>
        </form>
      </div>
    </div>
  );
};

export default TheBrief;
